

import React, { useState, useEffect } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Collapse,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, FONT_FAMILY, TYPOGRAPHY } from "../../constants/color";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import AppToast from "../../components/AppToast"; // Import AppToast

const textFieldSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    fontFamily: FONT_FAMILY,
    color: COLORS.PRIMARY,
    "& input": {
      color: COLORS.PRIMARY,
      height: "auto",
      py: 1,
    },
    "& textarea": {
      color: COLORS.PRIMARY,
      minHeight: 60, // Reduced from 80
      maxHeight: 80, // Added max height
      resize: "vertical",
      py: 1,
      px: 1.5,
    },
    "& fieldset": {
      borderColor: COLORS.LIGHT_SECONDARY,
    },
    "&:hover fieldset": {
      borderColor: COLORS.PRIMARY,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.PRIMARY,
    },
  },
  "& input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    opacity: 1,
  },
  "& textarea::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    opacity: 1,
  },
};

// Function to convert number to Roman numerals
const toRoman = (num) => {
  if (num < 1 || num > 3999) return num.toString();
  
  const romanNumerals = [
    { value: 1000, symbol: "M" },
    { value: 900, symbol: "CM" },
    { value: 500, symbol: "D" },
    { value: 400, symbol: "CD" },
    { value: 100, symbol: "C" },
    { value: 90, symbol: "XC" },
    { value: 50, symbol: "L" },
    { value: 40, symbol: "XL" },
    { value: 10, symbol: "X" },
    { value: 9, symbol: "IX" },
    { value: 5, symbol: "V" },
    { value: 4, symbol: "IV" },
    { value: 1, symbol: "I" },
  ];

  let result = "";
  for (const { value, symbol } of romanNumerals) {
    while (num >= value) {
      result += symbol;
      num -= value;
    }
  }
  return result;
};

const EditChecklistDrawer = ({ open, onClose, checklistData, onUpdate }) => {
  const { userData, isAdmin, isAuthenticated } = useAuth();
  
  const [mode, setMode] = useState("group");
  const [groupText, setGroupText] = useState("");
  const [taskText, setTaskText] = useState("");
  const [checklistTitle, setChecklistTitle] = useState("");
  const [checklistDescription, setChecklistDescription] = useState("");
  
  // NEW: Fields for task additional details
  const [showTestDescription, setShowTestDescription] = useState(false);
  const [showAcceptanceCriteria, setShowAcceptanceCriteria] = useState(false);
  const [testDescription, setTestDescription] = useState("");
  const [acceptanceCriteria, setAcceptanceCriteria] = useState("");
  
  const [sections, setSections] = useState([]);
  const [standaloneTasks, setStandaloneTasks] = useState([]);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // NEW: Use AppToast instead of Snackbar
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // Track dragging states - SIMPLIFIED
  const [draggedItem, setDraggedItem] = useState({
    type: null,
    sourceSectionIndex: null,
    sourceTaskIndex: null,
    isStandalone: false,
  });
  
  const [dropTarget, setDropTarget] = useState({
    sectionIndex: null,
    taskIndex: null,
    isStandalone: false,
  });

  // Load checklist data when drawer opens or data changes
  useEffect(() => {
    if (open && checklistData) {
      loadChecklistData();
    } else {
      resetForm();
    }
  }, [open, checklistData]);

  const loadChecklistData = () => {
    if (!checklistData) return;
    
    setChecklistTitle(checklistData.title || "");
    setChecklistDescription(checklistData.description || "");
    
    // Process sections data
    const newSections = [];
    const newStandaloneTasks = [];
    
    if (checklistData.sections && Array.isArray(checklistData.sections)) {
      checklistData.sections.forEach((section) => {
        if (section.name && section.name.trim() !== "") {
          // This is a group with name
          newSections.push({
            name: section.name,
            items: section.items?.map((item, index) => ({
              srNo: index + 1,
              question: item.question || "",
              testDescription: item.testDescription || "",
              acceptanceCriteria: item.acceptanceCriteria || "",
              ...item
            })) || []
          });
        } else {
          // This is standalone tasks (empty name)
          if (section.items && Array.isArray(section.items)) {
            section.items.forEach((item, index) => {
              newStandaloneTasks.push({
                srNo: index + 1,
                question: item.question || "",
                testDescription: item.testDescription || "",
                acceptanceCriteria: item.acceptanceCriteria || "",
                ...item
              });
            });
          }
        }
      });
    }
    
    setSections(newSections);
    setStandaloneTasks(newStandaloneTasks);
  };

  const resetForm = () => {
    setMode("group");
    setGroupText("");
    setTaskText("");
    setChecklistTitle("");
    setChecklistDescription("");
    setSections([]);
    setStandaloneTasks([]);
    // NEW: Reset additional fields
    setShowTestDescription(false);
    setShowAcceptanceCriteria(false);
    setTestDescription("");
    setAcceptanceCriteria("");
    setDraggedItem({
      type: null,
      sourceSectionIndex: null,
      sourceTaskIndex: null,
      isStandalone: false,
    });
    setDropTarget({
      sectionIndex: null,
      taskIndex: null,
      isStandalone: false,
    });
  };

  // NEW: Show toast notification
  const showToast = (type, title, message) => {
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  // NEW: Reset task form
  const resetTaskForm = () => {
    setTaskText("");
    setTestDescription("");
    setAcceptanceCriteria("");
    setShowTestDescription(false);
    setShowAcceptanceCriteria(false);
  };

  // Group functions
  const handleAddGroup = () => {
    if (!groupText.trim()) {
      showToast("error", "Error", "Please enter a group/subtitle name");
      return;
    }

    const existingGroup = sections.find(section => 
      section.name.toLowerCase() === groupText.trim().toLowerCase()
    );
    
    if (existingGroup) {
      showToast("error", "Error", "Group/subtitle already exists");
      return;
    }

    const newSection = {
      name: groupText.trim(),
      items: [],
    };

    setSections([...sections, newSection]);
    setGroupText("");
    showToast("success", "Success", "Group added successfully");
  };

  // Task functions - UPDATED to include additional fields
  const handleAddTask = () => {
    if (!taskText.trim()) {
      showToast("error", "Error", "Please enter a task");
      return;
    }

    // NEW: Create task with optional fields
    const newTask = {
      srNo: 0,
      question: taskText.trim(),
      ...(showTestDescription && testDescription.trim() && { testDescription: testDescription.trim() }),
      ...(showAcceptanceCriteria && acceptanceCriteria.trim() && { acceptanceCriteria: acceptanceCriteria.trim() }),
    };

    // If groups exist, add to the last group
    if (sections.length > 0) {
      const updatedSections = [...sections];
      const lastGroupIndex = updatedSections.length - 1;
      newTask.srNo = updatedSections[lastGroupIndex].items.length + 1;
      
      updatedSections[lastGroupIndex].items.push(newTask);
      setSections(updatedSections);
      
      showToast("success", "Success", `Task added to group: ${updatedSections[lastGroupIndex].name}`);
    } else {
      // If no groups exist, add as standalone
      newTask.srNo = standaloneTasks.length + 1;
      setStandaloneTasks([...standaloneTasks, newTask]);
      showToast("success", "Success", "Task added as standalone");
    }
    
    // NEW: Reset task form after adding
    resetTaskForm();
  };

  // Delete functions
  const handleDeleteStandaloneTask = (taskIndex) => {
    const updatedTasks = standaloneTasks.filter((_, index) => index !== taskIndex);
    const renumberedTasks = updatedTasks.map((task, index) => ({ ...task, srNo: index + 1 }));
    setStandaloneTasks(renumberedTasks);
    showToast("success", "Success", "Task deleted successfully");
  };

  const handleDeleteTask = (sectionIndex, taskIndex) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].items.splice(taskIndex, 1);
    updatedSections[sectionIndex].items = updatedSections[sectionIndex].items.map((item, index) => ({
      ...item,
      srNo: index + 1,
    }));
    setSections(updatedSections);
    showToast("success", "Success", "Task deleted successfully");
  };

  const handleDeleteGroup = (sectionIndex) => {
    // Get the group being deleted
    const groupToDelete = sections[sectionIndex];
    
    // Get all tasks from the group
    const tasksFromGroup = [...groupToDelete.items];
    
    // Remove the group from sections
    const updatedSections = sections.filter((_, index) => index !== sectionIndex);
    
    // Add tasks to standalone with updated serial numbers
    const updatedStandaloneTasks = [...standaloneTasks];
    let nextSrNo = updatedStandaloneTasks.length + 1;
    
    tasksFromGroup.forEach(task => {
      updatedStandaloneTasks.push({
        ...task,
        srNo: nextSrNo,
      });
      nextSrNo++;
    });
    
    // Update both states
    setSections(updatedSections);
    setStandaloneTasks(updatedStandaloneTasks);
    
    // Show notification
    showToast("success", "Success", `Group "${groupToDelete.name}" deleted. ${tasksFromGroup.length} task(s) moved to standalone.`);
  };

  // ========== DRAG & DROP FUNCTIONS ==========
  
  // Start dragging an item
  const handleDragStart = (e, type, sectionIndex, taskIndex, isStandalone) => {
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.style.opacity = "0.5";
    
    setDraggedItem({
      type,
      sourceSectionIndex: sectionIndex,
      sourceTaskIndex: taskIndex,
      isStandalone,
    });
  };

  // When dragging over a potential drop target
  const handleDragOver = (e, sectionIndex, taskIndex, isStandalone) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    
    setDropTarget({
      sectionIndex,
      taskIndex,
      isStandalone,
    });
  };

  // When dropping an item
  const handleDrop = (e, targetSectionIndex, targetTaskIndex, targetIsStandalone) => {
    e.preventDefault();
    e.stopPropagation();
    
    const { type, sourceSectionIndex, sourceTaskIndex, isStandalone: sourceIsStandalone } = draggedItem;
    
    // Reset drag states
    setDraggedItem({ type: null, sourceSectionIndex: null, sourceTaskIndex: null, isStandalone: false });
    setDropTarget({ sectionIndex: null, taskIndex: null, isStandalone: false });
    
    // Reset opacity
    const dragElements = document.querySelectorAll('[draggable="true"]');
    dragElements.forEach(el => el.style.opacity = "1");

    // Handle GROUP drag & drop
    if (type === 'group') {
      if (sourceSectionIndex !== null && targetSectionIndex !== null && 
          sourceSectionIndex !== targetSectionIndex) {
        const updatedSections = [...sections];
        const [draggedGroup] = updatedSections.splice(sourceSectionIndex, 1);
        updatedSections.splice(targetSectionIndex, 0, draggedGroup);
        setSections(updatedSections);
      }
      return;
    }

    // Handle TASK drag & drop
    if (type === 'task') {
      // Case 1: Dragging within standalone tasks
      if (sourceIsStandalone && targetIsStandalone) {
        if (sourceTaskIndex !== targetTaskIndex) {
          const updatedTasks = [...standaloneTasks];
          const [draggedTask] = updatedTasks.splice(sourceTaskIndex, 1);
          
          let adjustedIndex = targetTaskIndex;
          if (sourceTaskIndex < targetTaskIndex) {
            adjustedIndex = targetTaskIndex - 1;
          }
          
          updatedTasks.splice(adjustedIndex, 0, draggedTask);
          
          // Renumber
          const renumberedTasks = updatedTasks.map((task, index) => ({
            ...task,
            srNo: index + 1,
          }));
          
          setStandaloneTasks(renumberedTasks);
        }
      }
      // Case 2: Dragging within the same group
      else if (!sourceIsStandalone && !targetIsStandalone && 
               sourceSectionIndex === targetSectionIndex) {
        if (sourceTaskIndex !== targetTaskIndex) {
          const updatedSections = [...sections];
          const groupItems = [...updatedSections[sourceSectionIndex].items];
          const [draggedTask] = groupItems.splice(sourceTaskIndex, 1);
          
          let adjustedIndex = targetTaskIndex;
          if (sourceTaskIndex < targetTaskIndex) {
            adjustedIndex = targetTaskIndex - 1;
          }
          
          groupItems.splice(adjustedIndex, 0, draggedTask);
          
          // Renumber tasks in this group
          updatedSections[sourceSectionIndex].items = groupItems.map((item, index) => ({
            ...item,
            srNo: index + 1,
          }));
          
          setSections(updatedSections);
        }
      }
      // Case 3: Dragging between groups
      else if (!sourceIsStandalone && !targetIsStandalone && 
               sourceSectionIndex !== targetSectionIndex) {
        const updatedSections = [...sections];
        
        // Remove from source group
        const [draggedTask] = updatedSections[sourceSectionIndex].items.splice(sourceTaskIndex, 1);
        
        // Insert into target group
        let adjustedIndex = targetTaskIndex !== null ? targetTaskIndex : updatedSections[targetSectionIndex].items.length;
        updatedSections[targetSectionIndex].items.splice(adjustedIndex, 0, draggedTask);
        
        // Renumber both groups
        updatedSections[sourceSectionIndex].items = updatedSections[sourceSectionIndex].items.map((item, index) => ({
          ...item,
          srNo: index + 1,
        }));
        
        updatedSections[targetSectionIndex].items = updatedSections[targetSectionIndex].items.map((item, index) => ({
          ...item,
          srNo: index + 1,
        }));
        
        setSections(updatedSections);
      }
      // Case 4: Dragging from standalone to group
      else if (sourceIsStandalone && !targetIsStandalone) {
        const updatedSections = [...sections];
        const updatedStandalone = [...standaloneTasks];
        
        // Remove from standalone
        const [draggedTask] = updatedStandalone.splice(sourceTaskIndex, 1);
        
        // Add to group
        let adjustedIndex = targetTaskIndex !== null ? targetTaskIndex : updatedSections[targetSectionIndex].items.length;
        updatedSections[targetSectionIndex].items.splice(adjustedIndex, 0, draggedTask);
        
        // Renumber standalone
        const renumberedStandalone = updatedStandalone.map((task, index) => ({
          ...task,
          srNo: index + 1,
        }));
        
        // Renumber target group
        updatedSections[targetSectionIndex].items = updatedSections[targetSectionIndex].items.map((item, index) => ({
          ...item,
          srNo: index + 1,
        }));
        
        setStandaloneTasks(renumberedStandalone);
        setSections(updatedSections);
      }
      // Case 5: Dragging from group to standalone
      else if (!sourceIsStandalone && targetIsStandalone) {
        const updatedSections = [...sections];
        const updatedStandalone = [...standaloneTasks];
        
        // Remove from group
        const [draggedTask] = updatedSections[sourceSectionIndex].items.splice(sourceTaskIndex, 1);
        
        // Add to standalone
        let adjustedIndex = targetTaskIndex !== null ? targetTaskIndex : updatedStandalone.length;
        updatedStandalone.splice(adjustedIndex, 0, draggedTask);
        
        // Renumber source group
        updatedSections[sourceSectionIndex].items = updatedSections[sourceSectionIndex].items.map((item, index) => ({
          ...item,
          srNo: index + 1,
        }));
        
        // Renumber standalone
        const renumberedStandalone = updatedStandalone.map((task, index) => ({
          ...task,
          srNo: index + 1,
        }));
        
        setSections(updatedSections);
        setStandaloneTasks(renumberedStandalone);
      }
    }
  };

  // Handle drag end
  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    setDraggedItem({ type: null, sourceSectionIndex: null, sourceTaskIndex: null, isStandalone: false });
    setDropTarget({ sectionIndex: null, taskIndex: null, isStandalone: false });
  };

  // Update checklist - UPDATED to include additional fields
  const handleUpdateChecklist = async () => {
    if (!checklistTitle.trim()) {
      showToast("error", "Error", "Please enter checklist title");
      return;
    }

    const hasStandaloneTasks = standaloneTasks.length > 0;
    const hasGroupsWithTasks = sections.some(section => section.items.length > 0);
    
    if (!hasStandaloneTasks && !hasGroupsWithTasks) {
      showToast("error", "Error", "Please add at least one task");
      return;
    }

    if (!isAuthenticated || !isAdmin() || !userData?._id) {
      showToast("error", "Error",
        !isAuthenticated ? "You must be logged in" : 
        !isAdmin() ? "Only administrators can edit checklists" :
        "User ID not found"
      );
      return;
    }

    if (!checklistData?._id) {
      showToast("error", "Error", "Checklist ID not found");
      return;
    }

    setIsSubmitting(true);
    onClose();

    try {
      const allSections = [];
      
      // Add standalone tasks without "General" section name (empty name)
      if (standaloneTasks.length > 0) {
        allSections.push({
          name: "",
          items: standaloneTasks.map(task => ({ 
            srNo: task.srNo, 
            question: task.question,
            ...(task.testDescription && { testDescription: task.testDescription }),
            ...(task.acceptanceCriteria && { acceptanceCriteria: task.acceptanceCriteria }),
            ...(task._id && { _id: task._id })
          })),
        });
      }
      
      // Add regular groups with tasks
      sections.forEach(section => {
        if (section.items.length > 0) {
          allSections.push({
            name: section.name,
            items: section.items.map(item => ({ 
              srNo: item.srNo, 
              question: item.question,
              ...(item.testDescription && { testDescription: item.testDescription }),
              ...(item.acceptanceCriteria && { acceptanceCriteria: item.acceptanceCriteria }),
              ...(item._id && { _id: item._id })
            })),
          });
        }
      });

      const payload = {
        templateID: checklistData._id,
        title: checklistTitle.trim(),
        description: checklistDescription.trim(),
        sections: allSections,
      };

      const response = await fetch(`${url}/api/admin/checklist-templates/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok) {
        showToast("success", "Success", "Checklist updated successfully!");
        
        if (onUpdate) {
          onUpdate(data.data);
        }
        
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(data.message || "Failed to update checklist");
      }
    } catch (error) {
      console.error("Error updating checklist:", error);
      showToast("error", "Error", error.message || "Failed to update checklist");
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCreatorName = () => {
    if (!userData) return "Admin";
    return userData.name || userData.email || "Admin";
  };

  const getCurrentDateString = () => {
    const date = new Date();
    const options = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Helper to check if an item is being dragged over
  const isDragOver = (sectionIndex, taskIndex, isStandalone) => {
    return (
      dropTarget.sectionIndex === sectionIndex &&
      dropTarget.taskIndex === taskIndex &&
      dropTarget.isStandalone === isStandalone
    );
  };

  // NEW: Render additional fields section for tasks
// NEW: Render additional fields section for tasks
const renderTaskAdditionalFields = () => {
  if (mode !== "task") return null;

  return (
    <Box sx={{ mb: 3 }}>
      {/* Toggle buttons for additional fields */}
      <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={showTestDescription}
              onChange={(e) => setShowTestDescription(e.target.checked)}
              size="small"
              sx={{
                color: COLORS.SECONDARY,
                '&.Mui-checked': {
                  color: COLORS.SECONDARY,
                },
              }}
            />
          }
          label={
            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY }}>
              Add Test Description
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={showAcceptanceCriteria}
              onChange={(e) => setShowAcceptanceCriteria(e.target.checked)}
              size="small"
              sx={{
                color: COLORS.SECONDARY,
                '&.Mui-checked': {
                  color: COLORS.SECONDARY,
                },
              }}
            />
          }
          label={
            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY }}>
              Add Acceptance Criteria
            </Typography>
          }
        />
      </Box>

      {/* Additional fields */}
      <Collapse in={showTestDescription}>
        <Box sx={{ mb: 2 }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY, mb: 1 }}>
            Test Description
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            placeholder="Enter test description..."
            value={testDescription}
            onChange={(e) => setTestDescription(e.target.value)}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
                "& textarea": {
                  color: COLORS.PRIMARY,
                  minHeight: "auto",
                  maxHeight: "none",
                  resize: "vertical",
                  py: 1,
                  px: 1.5,
                },
                "& fieldset": {
                  borderColor: COLORS.LIGHT_SECONDARY,
                },
                "&:hover fieldset": {
                  borderColor: COLORS.PRIMARY,
                },
                "&.Mui-focused fieldset": {
                  borderColor: COLORS.PRIMARY,
                },
              },
              "& textarea::placeholder": {
                color: COLORS.LIGHT_SECONDARY,
                opacity: 1,
              },
            }}
          />
        </Box>
      </Collapse>

      <Collapse in={showAcceptanceCriteria}>
        <Box>
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.PRIMARY, mb: 1 }}>
            Acceptance Criteria
          </Typography>
          <TextField
            size="small"
            fullWidth
            multiline
            placeholder="Enter acceptance criteria..."
            value={acceptanceCriteria}
            onChange={(e) => setAcceptanceCriteria(e.target.value)}
            sx={{
              width: "100%",
              "& .MuiOutlinedInput-root": {
                fontFamily: FONT_FAMILY,
                color: COLORS.PRIMARY,
                "& textarea": {
                  color: COLORS.PRIMARY,
                  minHeight: "auto",
                  maxHeight: "none",
                  resize: "vertical",
                  py: 1,
                  px: 1.5,
                },
                "& fieldset": {
                  borderColor: COLORS.LIGHT_SECONDARY,
                },
                "&:hover fieldset": {
                  borderColor: COLORS.PRIMARY,
                },
                "&.Mui-focused fieldset": {
                  borderColor: COLORS.PRIMARY,
                },
              },
              "& textarea::placeholder": {
                color: COLORS.LIGHT_SECONDARY,
                opacity: 1,
              },
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
};

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: 700,
            p: 3,
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography sx={{ ...TYPOGRAPHY.H2, fontFamily: FONT_FAMILY, color: COLORS.PRIMARY }}>
            Edit Check List
          </Typography>
          <IconButton onClick={onClose} sx={{ ml: "auto", color: COLORS.ERROR, "&:hover": { color: COLORS.PRIMARY } }}>
            <Icon name="close" />
          </IconButton>
        </Box>

        {/* Name + Created By */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY }}>Name of Check List Type*</Typography>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, whiteSpace: "nowrap" }}>
              Created by: {getCreatorName()} on {getCurrentDateString()}
            </Typography>
          </Box>
          <TextField
            fullWidth
            size="small"
            placeholder="CheckList Type Name"
            value={checklistTitle}
            onChange={(e) => setChecklistTitle(e.target.value)}
            sx={textFieldSx}
          />
        </Box>

        <Divider sx={{ mb: 2, borderColor: COLORS.LIGHT_SECONDARY }} />

        {/* Add Group / Task buttons */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mb: 3 }}>
          <Button
            startIcon={<Icon name="plus2" />}
            onClick={() => {
              setMode("group");
              resetTaskForm();
            }}
            sx={{
              height: 34,
              px: 2,
              borderRadius: 2,
              fontSize: 13,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              backgroundColor: mode === "group" ? COLORS.SECONDARY : COLORS.LIGHT_SECONDARY,
              color: mode === "group" ? COLORS.BACKGROUND : COLORS.TEXT,
              "&:hover": { backgroundColor: mode === "group" ? COLORS.SECONDARY : COLORS.LIGHT_SECONDARY },
            }}
          >
            Add Group
          </Button>
          
          <Button
            startIcon={<Icon name="plus2" />}
            onClick={() => setMode("task")}
            sx={{
              height: 34,
              px: 2,
              borderRadius: 2,
              fontSize: 13,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              backgroundColor: mode === "task" ? COLORS.SECONDARY : COLORS.LIGHT_SECONDARY,
              color: mode === "task" ? COLORS.BACKGROUND : COLORS.TEXT,
              "&:hover": { backgroundColor: mode === "task" ? COLORS.SECONDARY : COLORS.LIGHT_SECONDARY },
            }}
          >
            Add Task
          </Button>
        </Box>

        {/* Create Group / Task */}
        <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.PRIMARY, mb: 1 }}>
          {mode === "group" ? "Create New Group/ Subtitle:" : "Create New Task:"}
        </Typography>

        <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
          <TextField
            size="small"
            fullWidth
            placeholder={mode === "group" ? "Create New Group/ Subtitle" : "Create New Task"}
            value={mode === "group" ? groupText : taskText}
            onChange={(e) => mode === "group" ? setGroupText(e.target.value) : setTaskText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (mode === "group" ? handleAddGroup() : handleAddTask())}
            sx={textFieldSx}
          />
          <Button
            onClick={mode === "group" ? handleAddGroup : handleAddTask}
            sx={{
              minWidth: 120,
              height: 42,
              borderRadius: 2,
              backgroundColor: COLORS.SECONDARY,
              color: COLORS.BACKGROUND,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              "&:hover": { backgroundColor: COLORS.SECONDARY },
            }}
          >
            {mode === "group" ? "Add Group" : "Add Task"}
          </Button>
        </Box>

        {/* NEW: Additional fields for tasks */}
        {renderTaskAdditionalFields()}

        <Divider sx={{ mb: 3, borderColor: COLORS.LIGHT_SECONDARY }} />

        {/* Checklist Items */}
        {(sections.length > 0 || standaloneTasks.length > 0) && (
          <>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, mb: 1 }}>Checklist Item</Typography>
            <Divider sx={{ mb: 1, borderColor: COLORS.LIGHT_SECONDARY }} />

            {/* Standalone Tasks */}
            {standaloneTasks.length > 0 && (
              <Box sx={{ mb: sections.length > 0 ? 3 : 0 }}>
                {standaloneTasks.map((task, taskIndex) => (
                  <Box
                    key={task._id || taskIndex}
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                      py: 1.5,
                      px: 1,
                      borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                      cursor: 'move',
                      userSelect: 'none',
                      transition: 'all 0.2s ease',
                      backgroundColor: isDragOver(null, taskIndex, true) 
                        ? COLORS.LIGHT_SECONDARY + '40' 
                        : draggedItem.sourceTaskIndex === taskIndex && draggedItem.isStandalone
                        ? COLORS.SECONDARY + '20'
                        : 'transparent',
                      '&:last-child': { borderBottom: sections.length > 0 ? `1px solid ${COLORS.LIGHT_SECONDARY}` : 'none' },
                    }}
                    draggable
                    onDragStart={(e) => handleDragStart(e, 'task', null, taskIndex, true)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleDragOver(e, null, taskIndex, true)}
                    onDrop={(e) => handleDrop(e, null, taskIndex, true)}
                  >
                    <Typography sx={{ minWidth: 24, textAlign: 'right', fontWeight: 500 }}>{task.srNo}.</Typography>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.PRIMARY, mb: 0.5 }}>
                        {task.question}
                      </Typography>
                      
                      {/* NEW: Display Test Description if exists */}
                      {task.testDescription && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, fontWeight: 600, color: COLORS.SECONDARY, mb: 0.5 }}>
                            Test Description:
                          </Typography>
                          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT }}>
                            {task.testDescription}
                          </Typography>
                        </Box>
                      )}
                      
                      {/* NEW: Display Acceptance Criteria if exists */}
                      {task.acceptanceCriteria && (
                        <Box sx={{ mt: 1 }}>
                          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, fontWeight: 600, color: COLORS.SECONDARY, mb: 0.5 }}>
                            Acceptance Criteria:
                          </Typography>
                          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT }}>
                            {task.acceptanceCriteria}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: 'center', minWidth: 60 }}>
                      <Icon name="delete" onClick={() => handleDeleteStandaloneTask(taskIndex)} color={COLORS.ERROR} size={18} style={{ cursor: "pointer" }} />
                      <Icon name="grip-vertical" color={COLORS.LIGHT_SECONDARY} size={18} style={{ cursor: 'move' }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Groups */}
            {sections.map((section, sectionIndex) => (
              <Box 
                key={sectionIndex} 
                sx={{ 
                  mb: 3,
                  border: isDragOver(sectionIndex, null, false) 
                    ? `2px dashed ${COLORS.SECONDARY}` 
                    : '1px solid transparent',
                  borderRadius: 1,
                  transition: 'all 0.2s ease',
                  backgroundColor: isDragOver(sectionIndex, null, false) 
                    ? COLORS.LIGHT_SECONDARY + '20' 
                    : 'transparent',
                }}
                draggable
                onDragStart={(e) => handleDragStart(e, 'group', sectionIndex, null, false)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, sectionIndex, null, false)}
                onDrop={(e) => handleDrop(e, sectionIndex, null, false)}
              >
                {/* Group Header */}
                <Box 
                  sx={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center", 
                    mb: 1,
                    p: 1.5,
                    cursor: 'move',
                    userSelect: 'none',
                    borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                    backgroundColor: draggedItem.sourceSectionIndex === sectionIndex && draggedItem.type === 'group'
                      ? COLORS.SECONDARY + '20'
                      : 'transparent',
                  }}
                >
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT, fontWeight: 600 }}>
                    {toRoman(sectionIndex + 1)}. {section.name}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, minWidth: 60 }}>
                    <Icon name="delete" onClick={() => handleDeleteGroup(sectionIndex)} color={COLORS.ERROR} size={18} style={{ cursor: "pointer" }} />
                    <Icon name="grip-vertical" color={COLORS.LIGHT_SECONDARY} size={18} style={{ cursor: 'move' }} />
                  </Box>
                </Box>

                {/* Tasks within group */}
                <Box sx={{ p: 1 }}>
                  {section.items.length > 0 ? (
                    section.items.map((item, itemIndex) => (
                      <Box
                        key={item._id || itemIndex}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          py: 1.5,
                          px: 1,
                          borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                          cursor: 'move',
                          userSelect: 'none',
                          transition: 'all 0.2s ease',
                          backgroundColor: isDragOver(sectionIndex, itemIndex, false)
                            ? COLORS.LIGHT_SECONDARY + '40'
                            : draggedItem.sourceSectionIndex === sectionIndex && 
                              draggedItem.sourceTaskIndex === itemIndex && 
                              !draggedItem.isStandalone
                            ? COLORS.SECONDARY + '20'
                            : 'transparent',
                          '&:last-child': { borderBottom: 'none' },
                        }}
                        draggable
                        onDragStart={(e) => {
                          e.stopPropagation();
                          handleDragStart(e, 'task', sectionIndex, itemIndex, false);
                        }}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => {
                          e.stopPropagation();
                          handleDragOver(e, sectionIndex, itemIndex, false);
                        }}
                        onDrop={(e) => {
                          e.stopPropagation();
                          handleDrop(e, sectionIndex, itemIndex, false);
                        }}
                      >
                        <Typography sx={{ minWidth: 24, textAlign: 'right', fontWeight: 500 }}>{item.srNo}.</Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ ...TYPOGRAPHY.BODY_STANDARD, color: COLORS.PRIMARY, mb: 0.5 }}>
                            {item.question}
                          </Typography>
                          
                          {/* NEW: Display Test Description if exists */}
                          {item.testDescription && (
                            <Box sx={{ mt: 1, mb: 1 }}>
                              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, fontWeight: 600, color: COLORS.SECONDARY, mb: 0.5 }}>
                                Test Description:
                              </Typography>
                              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT }}>
                                {item.testDescription}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* NEW: Display Acceptance Criteria if exists */}
                          {item.acceptanceCriteria && (
                            <Box sx={{ mt: 1 }}>
                              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, fontWeight: 600, color: COLORS.SECONDARY, mb: 0.5 }}>
                                Acceptance Criteria:
                              </Typography>
                              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT }}>
                                {item.acceptanceCriteria}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ display: "flex", gap: 1, alignItems: 'center', minWidth: 60 }}>
                          <Icon name="delete" onClick={() => handleDeleteTask(sectionIndex, itemIndex)} color={COLORS.ERROR} size={18} style={{ cursor: "pointer" }} />
                          <Icon name="grip-vertical" color={COLORS.LIGHT_SECONDARY} size={18} style={{ cursor: 'move' }} />
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT, fontStyle: 'italic', textAlign: 'center', py: 2 }}>
                      No tasks added to this group yet
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </>
        )}

        {/* No items message */}
        {sections.length === 0 && standaloneTasks.length === 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.MUTED_TEXT, fontStyle: 'italic', textAlign: 'center', py: 4 }}>
              No checklist items added yet. Add groups or tasks using the buttons above.
            </Typography>
          </Box>
        )}

        {/* Footer */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <Button
            startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <Icon name="save" />}
            onClick={handleUpdateChecklist}
            disabled={isSubmitting || !isAdmin()}
            sx={{
              px: 3,
              height: 42,
              backgroundColor: COLORS.SECONDARY,
              color: COLORS.BACKGROUND,
              textTransform: "none",
              fontFamily: FONT_FAMILY,
              "&:hover": { backgroundColor: COLORS.SECONDARY },
              "&.Mui-disabled": { backgroundColor: COLORS.LIGHT_SECONDARY, color: COLORS.MUTED_TEXT },
            }}
          >
            {isSubmitting ? "Updating..." : "Update Check List"}
          </Button>
        </Box>

        {/* Admin permission warning */}
        {!isAdmin() && (
          <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.ERROR, mt: 1, textAlign: "center" }}>
            Only administrators can edit checklists
          </Typography>
        )}
      </Drawer>

      {/* NEW: Toast Notification */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </>
  );
};

export default EditChecklistDrawer;